import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import './Profile.css';
import ProfileHeader from '../components/Header/ProfileHeader';
import Section from '../components/Section';
import { profile } from '../data/profile';
import { NAV_SECTIONS } from '../config/sections';
import { downloadProfileAsPDF } from '../utils/pdfUtils';
import ScrollToTopButton from '../components/ScrollToTopButton/ScrollToTopButton';
import WhatsAppButton from '../components/WhatsAppButton/WhatsAppButton';
import AccessibleTools from '../components/AccessibleTools/AccessibleTools';
import ReactGA from 'react-ga';

const MOBILE_BREAKPOINT = 768;
const TOPBAR_HEIGHT     = 58;

const Profile: React.FC = () => {
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
    const [activeSection, setActiveSection] = useState('section-about');
    const [scrollProgress, setScrollProgress] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const sectionRefs = useRef<(HTMLElement | null)[]>([]);
    useEffect(() => {
        sectionRefs.current = sectionRefs.current.slice(0, NAV_SECTIONS.length);
    }, []);

    useEffect(() => {
        if (drawerOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [drawerOpen]);

    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth > MOBILE_BREAKPOINT && drawerOpen) {
                setDrawerOpen(false);
            }
        };
        window.addEventListener('resize', onResize, { passive: true });
        return () => window.removeEventListener('resize', onResize);
    }, [drawerOpen]);

    useEffect(() => {
        const revealObserver = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
            { threshold: 0.08 }
        );
        sectionRefs.current.forEach(r => r && revealObserver.observe(r));

        const activeObserver = new IntersectionObserver(
            entries => entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }),
            { threshold: 0.2, rootMargin: '0px 0px -45% 0px' }
        );
        sectionRefs.current.forEach(r => r && activeObserver.observe(r));

        const onScroll = () => {
            const h = document.documentElement.scrollHeight - window.innerHeight;
            setScrollProgress(h > 0 ? (window.scrollY / h) * 100 : 0);
        };
        window.addEventListener('scroll', onScroll, { passive: true });

        return () => {
            revealObserver.disconnect();
            activeObserver.disconnect();
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    const scrollToSection = useCallback((id: string) => {
        const el = document.getElementById(id);
        if (!el) return;

        const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
        const offset   = isMobile ? TOPBAR_HEIGHT + 12 : 24;

        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
        setDrawerOpen(false);
        el.setAttribute('tabindex', '-1');
        el.focus({ preventScroll: true });
    }, []);

    const handleDownload = useCallback(() => {
        setIsGeneratingPDF(true);
        ReactGA.event({ category: 'User', action: 'Downloaded PDF' });
        downloadProfileAsPDF(profile)
            .catch(() => alert('Could not generate CV. Please try again.'))
            .finally(() => setIsGeneratingPDF(false));
    }, []);

    const getSectionData = (dataKey?: string) => {
        if (!dataKey) return undefined;
        const data = (profile as any)[dataKey];
        if (dataKey === 'contact') return profile.contact;
        return data;
    };

    const getSectionCount = (sec: typeof NAV_SECTIONS[number]): number | undefined => {
        if (sec.dataKey === 'experience') return profile.experience.length;
        if (sec.dataKey === 'education') return profile.education.length;
        if (sec.dataKey === 'projects') return profile.projects.length;
        if (sec.dataKey === 'accomplishments') return profile.accomplishments.certifications.length;
        if (sec.dataKey === 'languages') return profile.languages.length;
        return undefined;
    };

    if (!profile) return (
        <div className="loading-container" role="status" aria-live="polite">
            <div className="loading-content">
                <div className="loading-spinner" aria-hidden="true" />
                <p className="loading-text">Loading portfolio…</p>
            </div>
        </div>
    );

    return (
        <>
            <a href="#main-content" className="skip-link">Skip to main content</a>

            <Helmet>
                <title>Ali Rajab — Software Engineer & Product Manager</title>
                <meta name="description" content="Portfolio of Ali Rajab — Software Engineer, Product Manager, and Mobile App Developer specialising in Flutter, Laravel, Web3, and SUI Network." />
                <meta property="og:title" content="Ali Rajab | Software Engineer & Product Manager" />
                <meta property="og:description" content="Explore Ali Rajab's portfolio: Flutter, Laravel, React.js, Web3, SUI Network." />
                <meta property="og:url" content="https://alirajab12.github.io/" />
            </Helmet>

            <div
                className="scroll-progress"
                role="progressbar"
                aria-valuenow={Math.round(scrollProgress)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Page scroll progress"
            >
                <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} />
            </div>

            <div className="app-shell">
                <aside
                    className={`sidebar ${drawerOpen ? 'drawer-open' : ''}`}
                    aria-label="Profile sidebar"
                >
                    <ProfileHeader
                        profile={profile}
                        onDownload={handleDownload}
                        isGenerating={isGeneratingPDF}
                    />

                    <nav className="sidebar-nav" aria-label="Portfolio sections">
                        <p className="sidebar-nav-label">Navigation</p>
                        <ul>
                            {NAV_SECTIONS.map(sec => (
                                <li key={sec.key}>
                                    <button
                                        className={`sidebar-nav-link ${activeSection === sec.id ? 'active' : ''}`}
                                        onClick={() => scrollToSection(sec.id)}
                                        aria-current={activeSection === sec.id ? 'location' : undefined}
                                    >
                                        <span className="nav-icon" aria-hidden="true">{sec.icon}</span>
                                        {sec.title}
                                        {activeSection === sec.id && (
                                            <span className="nav-active-dot" aria-hidden="true" />
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                {drawerOpen && (
                    <div
                        className="drawer-overlay"
                        onClick={() => setDrawerOpen(false)}
                        aria-hidden="true"
                    />
                )}

                <main className="main-content" id="main-content" tabIndex={-1}>
                    <div className="mobile-topbar">
                        <button
                            className="mobile-menu-btn"
                            onClick={() => setDrawerOpen(o => !o)}
                            aria-expanded={drawerOpen}
                            aria-controls="sidebar-nav"
                            aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'}
                        >
                            <span className={`hamburger ${drawerOpen ? 'open' : ''}`} aria-hidden="true">
                                <span /><span /><span />
                            </span>
                        </button>
                        <span className="mobile-brand">{profile.name}</span>
                        <div className="topbar-right-spacer" aria-hidden="true" />
                    </div>

                    <div className="sections-wrapper">
                        {NAV_SECTIONS.map((sec, index) => (
                            <Section
                                key={sec.id}
                                title={sec.title}
                                id={sec.id}
                                count={getSectionCount(sec)}
                                ref={el => { sectionRefs.current[index] = el; }}
                            >
                                {sec.key === 'about' ? (
                                    <p className="about-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(profile.summary) }} />
                                ) : sec.component ? (
                                    <sec.component {...{ [sec.dataKey!]: getSectionData(sec.dataKey) }} />
                                ) : null}
                            </Section>
                        ))}
                    </div>

                    <ScrollToTopButton />
                    <AccessibleTools />
                    {profile.contact.whatsapp && (
                        <WhatsAppButton phoneNumber={profile.contact.whatsapp} />
                    )}
                </main>
            </div>
        </>
    );
};

export default React.memo(Profile);
